/**
 * Volume-Aware Prompt Enhancer Configuration
 * 
 * Provides system prompts for the prompt enhancement feature that are 
 * context-aware based on the selected volume level and issue type.
 */

import { ISSUE_TYPE } from '../screen/GenerationPanel/Generationpanel.types';
import type { VolumeLevel } from './volumePrompts';

export type { VolumeLevel } from './volumePrompts';

/**
 * Build a system prompt for the prompt enhancer based on volume and issue type
 * 
 * @param volume - The selected volume level
 * @param issueType - The type of issues being generated
 * @returns A formatted system prompt string
 */
export const buildEnhancerSystemPrompt = (
  volume: VolumeLevel,
  issueType?: ISSUE_TYPE
): string => {
  const basePrompt = `You are an AI assistant that expands brief project descriptions 
into comprehensive feature outlines for agile project planning.`;

  const volumeContext: Record<VolumeLevel, string> = {
    low: `The user needs a lean, focused breakdown. Expand their input into a 
concise outline covering only the essential features and core functionality. 
Avoid over-elaboration. Aim for a minimal but complete representation.`,
    
    medium: `The user needs a balanced breakdown. Expand their input into a 
well-structured outline covering main features, key integrations, and 
important user flows. Provide a reasonable level of detail without excess.`,
    
    high: `The user needs a comprehensive breakdown. Expand their input into 
an extensive outline covering all features, edge cases, integrations, 
non-functional requirements, security considerations, and supporting 
capabilities like monitoring and documentation.`
  };

  const issueTypeContext: Partial<Record<ISSUE_TYPE, string>> = {
    [ISSUE_TYPE.MultipleStories]: `Focus on user-centric functionality that can be broken 
into discrete, estimable User Stories.`,
    [ISSUE_TYPE.MultipleEpics]: `Think in terms of large functional areas that can serve 
as Epic containers for multiple related Stories.`,
    [ISSUE_TYPE.EpicsAndStories]: `Structure the outline hierarchically with 
major areas (Epics) and their constituent features (Stories).`,
    [ISSUE_TYPE.SingleEpic]: `Create a single comprehensive Epic description covering all 
aspects of this feature area.`,
    [ISSUE_TYPE.SingleStory]: `Create a single detailed User Story with clear acceptance 
criteria and scope.`,
    [ISSUE_TYPE.SingleEpicMultiStories]: `Create one Epic with multiple related User Stories 
that together deliver the feature.`
  };

  const formatGuidance = `Format the output as a hierarchical bullet-point list 
with main categories and sub-items. Do not include any commentary outside 
the outline itself. Use clear, actionable language.`;

  const parts = [basePrompt, volumeContext[volume]];
  
  const typeContext = issueType ? issueTypeContext[issueType] : undefined;
  if (typeContext) {
    parts.push(typeContext);
  }
  
  parts.push(formatGuidance);

  return parts.join('\n\n');
};

/**
 * Get the default system prompt for backward compatibility
 * Uses medium volume and no specific issue type
 */
export const getDefaultEnhancerPrompt = (): string => {
  return buildEnhancerSystemPrompt('medium');
};
